import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Switch } from "./ui/switch";
import infoIcon2 from "../assets/icons/info_icon.svg";
import { Button } from "./ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  addPayAddress,
  deletePayAddress,
  getPayAddresses,
} from "../services/settingsService";
import { useToast } from "../hooks/use-toast";

export default function DonationsView({ organisation }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isInAddressBook, setIsInAddressBook] = useState(false);
  const [addressId, setAddressId] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const orgPayId = organisation?.payID || organisation?.user_id?._id;
  const orgName = organisation?.organisation_info?.org_name || "";

  // Check if organisation is already in address book
  useEffect(() => {
    const checkAddressBook = async () => {
      try {
        const response = await getPayAddresses();
        if (response?.data?.data?.addresses) {
          const addresses = response.data.data.addresses;
          const found = addresses.find(
            (addr) => addr.payId === orgPayId || addr.name === orgName
          );
          if (found) {
            setIsInAddressBook(true);
            setAddressId(found._id);
          }
        }
      } catch (error) {
        console.error("Error checking address book:", error);
      }
    };
    if (orgPayId || orgName) {
      checkAddressBook();
    }
  }, [orgPayId, orgName]);

  const handleAddressBookToggle = async (checked) => {
    setLoading(true);
    try {
      if (checked) {
        // Add to address book
        const addressData = {
          name: orgName,
          lastname:
            organisation?.organisation_info?.org_category || "Organisation",
          payId: orgPayId,
        };
        const result = await addPayAddress(addressData);
        console.log("Add address result:", result);
        setIsInAddressBook(true);
        setAddressId(result?.data?.data?._id);
        toast({
          description: "Organisation added to address book",
          variant: "eighty",
        });
      } else {
        // Remove from address book
        if (addressId) {
          await deletePayAddress(addressId);
        }
        setIsInAddressBook(false);
        setAddressId(null);
        toast({
          description: "Organisation removed from address book",
          variant: "eighty",
        });
      }
    } catch (error) {
      console.error("Address book error:", error);
      toast({
        description: "Failed to update address book",
        variant: "destructive",
      });
      // Revert state on error
      setIsInAddressBook(!checked);
    } finally {
      setLoading(false);
    }
  };

  const toggleAbout = () => {
    setIsOpen(!isOpen);
  };
  return (
    <div className="flex justify-center mt-12 pb-10 px-4 md:px-16 lg:px-32">
      <div className="max-w-3xl w-full">
        <Card className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <CardHeader className="px-9 pt-6 pb-4">
            <CardTitle className="text-2xl font-semibold text-text-secondary text-[20px]">
              <div className="flex items-center">
                <img
                  src={organisation?.organisation_info?.org_image_url}
                  alt={organisation?.organisation_info?.org_name}
                  width={70}
                  height={70}
                  className="mr-4 object-contain"
                />
                <div className="text-[32px] text-text-primary">
                  {organisation?.organisation_info?.org_name}
                  <p className="text-text-secondary text-[16px] font-normal">
                    {organisation?.organisation_info?.org_short_description}
                  </p>
                </div>
              </div>
            </CardTitle>
          </CardHeader>

          <CardContent className="px-9 pt-2  pb-6">
            <div className="columns-1 mb-6">
              <img
                src={organisation?.organisation_info?.org_image_url}
                alt={organisation?.organisation_info?.org_name}
                className="h-[280px] w-full rounded-[25px] object-cover"
              />
            </div>
            <div className="columns-1 mb-6">
              <p className="text-[14px] text-text-secondary font-semibold mb-2">
                About
              </p>
              <div className="relative w-full border-[1px] rounded-[12px] border-bg-border ">
                <div className="flex justify-between border-bg-border rounded-t-[12px] w-full  focus:ring-2 focus:ring-purple-400 transition bg-white px-5 py-5">
                  <p className=" text-[14px] font-normal ">
                    {organisation?.organisation_info?.org_short_description}
                  </p>
                </div>
                <div
                  className="border-t-[1px] border-bg-border rounded-b-[12px] text-sm w-full focus:ring-2 focus:ring-purple-400 transition bg-white text-[14px] font-normal px-5 py-5 hover:bg-gray-50 cursor-pointer"
                  onClick={toggleAbout}
                >
                  <div className="flex justify-between items-center">
                    <p className="text-[14px] font-normal">
                      {isOpen ? "See less" : "See more"}
                    </p>
                    {isOpen ? (
                      <ChevronUp
                        className="text-text-secondary"
                        strokeWidth={1}
                      />
                    ) : (
                      <ChevronDown
                        className="text-text-secondary"
                        strokeWidth={1}
                      />
                    )}
                  </div>
                  {isOpen && (
                    <div className="mt-4" onClick={(e) => e.stopPropagation()}>
                      <p className="text-[14px] font-normal">
                        {organisation?.organisation_info?.org_description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="columns-1 mb-6">
              <div className="flex justify-between border-[1px] border-bg-border rounded-[12px] text-sm w-full focus:ring-2 focus:ring-purple-400 transition bg-white text-[14px] font-normal px-5 py-5">
                <div className="flex items-center w-[50%]">
                  <p className="text-[14px] font-normal">Add to address book</p>
                  <img
                    className="ml-2"
                    height="15px"
                    width="15px"
                    src={infoIcon2}
                    alt="info"
                  />
                </div>
                <div>
                  <Switch
                    checked={isInAddressBook}
                    onCheckedChange={handleAddressBookToggle}
                    disabled={loading}
                    className={`data-[state=checked]:bg-accent-pink ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-center py-3">
              <Button
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 rounded-full w-[40%]"
                onClick={() => {
                  navigate("/settings/make-donation", {
                    state: {
                      donation: organisation,
                    },
                  });
                }}
              >
                Make Donation
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
